import React from 'react';
import Modal from '@atlaskit/modal-dialog';
import styled from 'styled-components';

import Badge from 'kingpin/atoms/Badge';
import Button from 'kingpin/atoms/Button';
import Typography from 'kingpin/atoms/Typography';
import Row from 'components/Common/Row';
import FilterPlates from 'components/MetricsInfoButton/MetricInfoPopup/FilterPlates';

import {
  FILTER_SOURCE_LABELS,
  filtersForIssue,
  ISSUE_BADGES,
  ISSUE_FIXES,
  ISSUE_LABELS,
  SEVERITY_LABELS,
  severityFor,
} from './constants';

// Everything wrong with ONE report, and what to do about each of it.
//
// The row badges say what is wrong at a glance; this says why it matters and
// where to change it. Keeping the detail here rather than in the Suggestions
// list means a finding lives in exactly one place — beside the report it is
// about.

const ContentDiv = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 70vh;
  overflow-y: auto;
`;

const IssueDiv = styled.div`
  border: 1px solid var(--color-border-secondary);
  border-radius: 8px;
  padding: 12px 16px;
`;

const FilterListUl = styled.ul`
  margin: 8px 0 0 0;
  padding-left: 20px;
`;

const PlatesDiv = styled.div`
  margin: 4px 0 8px 0;
`;

const FixDiv = styled.div`
  margin-top: 8px;
  padding: 8px 12px;
  border-radius: 6px;
  background-color: var(--color-bg-secondary);
`;

const ReportIssuesModal = ({
  report,
  findings,
  clusters,
  close,
}: {
  report: AiReady.AnalysedReport;
  // Every finding about this report — the analyser's own issues plus the ones
  // only visible across reports, like redundancy.
  findings: AiReady.Suggestion[];
  // Name collisions this report takes part in. The siblings live on OTHER
  // reports, so the comparison cannot be built from `report` alone.
  clusters: AiReady.AmbiguityCluster[];
  close: () => void;
}) => (
  <Modal width="large" onClose={close} shouldScrollInViewport>
    <ContentDiv data-testid="ai-ready-issues-modal">
      <Row centerAlign spaceBetween>
        <Typography.Header type="H4">
          What needs fixing on {report.reportName}
        </Typography.Header>
        <Button type="Ghost" size="Small" label="Close" onClick={close} />
      </Row>

      {findings.length === 0 && (
        <Typography.Body type="Body 13" color="var(--color-text-secondary)">
          Nothing — this report is well formed for the agent.
        </Typography.Body>
      )}

      {findings.map((finding) => {
        const severity = severityFor(finding.kind);
        const offendingFilters = filtersForIssue({
          kind: finding.kind,
          filters: report.filters,
        });
        const isAmbiguity = finding.kind === 'ambiguousMetrics';
        const collisions = isAmbiguity
          ? clusters.filter((cluster) =>
              cluster.members.some((member) =>
                report.metricIds.includes(member.id),
              ),
            )
          : [];
        return (
          <IssueDiv
            key={finding.id}
            data-testid={`ai-ready-issue-${finding.kind}`}
          >
            <Row centerAlign style={{ gap: 8, marginBottom: 4 }}>
              {/* Only broken is badged. Everything in this modal needs fixing,
                  so labelling most of it "Degraded" said nothing — the absence
                  of a badge now means "degraded", and the one badge that shows
                  up is the one worth reacting to. */}
              {severity === 'error' && (
                <Badge
                  badgeType={ISSUE_BADGES[severity]}
                  text={SEVERITY_LABELS[severity]}
                />
              )}
              <Typography.Body type="Body 13 Bold">
                {ISSUE_LABELS[finding.kind]}
              </Typography.Body>
            </Row>
            {/* A collision is settled by comparing the metrics, not by
                reading about it: dataset and filters ARE the difference, and
                the filters are the one thing the agent never sees. */}
            {isAmbiguity ? (
              <FilterListUl>
                {collisions.flatMap((cluster) =>
                  cluster.members.map((member) => (
                    <li key={member.id}>
                      <Typography.Body type="Body 12">
                        {member.name}
                        {member.status === 'core' ? ' · core' : ''}
                        {member.datasets.length
                          ? ` · ${member.datasets.join(', ')}`
                          : ''}
                        {member.savedFilterNames.length
                          ? ` · ${member.savedFilterNames.join(' and ')}`
                          : ''}
                        {member.reportNames.length
                          ? ` · on ${member.reportNames.join(', ')}`
                          : ''}
                      </Typography.Body>
                      {/* Plated rather than written out: comparing two
                          same-named metrics is a side-by-side read of exactly
                          this, and prose makes that harder than it needs to
                          be. */}
                      {member.filters &&
                      Object.keys(member.filters).length > 0 ? (
                        <PlatesDiv>
                          <FilterPlates filters={member.filters} />
                        </PlatesDiv>
                      ) : (
                        <Typography.Body
                          type="Body 12"
                          color="var(--color-text-secondary)"
                        >
                          No filters
                        </Typography.Body>
                      )}
                    </li>
                  )),
                )}
              </FilterListUl>
            ) : (
              <>
                <Typography.Body
                  type="Body 12"
                  color="var(--color-text-secondary)"
                >
                  {finding.reason}
                </Typography.Body>
                {offendingFilters.length > 0 && (
                  <FilterListUl>
                    {offendingFilters.map((filter) => (
                      <li key={filter.key}>
                        <Typography.Body type="Body 12">
                          {FILTER_SOURCE_LABELS[filter.source]}:{' '}
                          {filter.description}
                        </Typography.Body>
                      </li>
                    ))}
                  </FilterListUl>
                )}
                <FixDiv>
                  <Typography.Body type="Body 12">
                    {/* Where the finding's own detail already says what to
                        change, it beats generic advice: "SE only, not
                        published" names the two flags to clear, where "clear
                        whichever made it unreachable" makes the reader go and
                        find out which. */}
                    {finding.kind === 'unreachableAiReady' && finding.detail
                      ? finding.detail
                      : ISSUE_FIXES[finding.kind]}
                  </Typography.Body>
                </FixDiv>
              </>
            )}
          </IssueDiv>
        );
      })}
    </ContentDiv>
  </Modal>
);

export default ReportIssuesModal;
